import { Language } from '@nx-smartmonkey/shared/domain';
import { logger } from '@sentry/utils';
import * as mixpanel from 'mixpanel-browser';

import { sessionService } from '../application/SessionService';
import { PLAN_TYPE } from '../constants/Subscriptions';
import { GeoFenceDTO } from '../domain/geoFence/GeoFenceDTO';
import { PlanType } from '../domain/subscription/SubscriptionDTO';
import { Task } from '../domain/task/Task';
import { User } from '../domain/user/User';
import configuration from './configuration';
import { INTERCOM_EVENTS, intercom } from './intercom';

export const PageViewPages = {
  login: `Login`,
  register: `Register`,
  plans: `Plans`,
  clients: `Clients`,
  vehicles: `Vehicles`,
  settings: `Configuration`,
  backoffice: `Administration`,
  supervisors: `Supervisors`,
  analytics: {
    summary: `Analytics summary`,
    planner: {
      plans: `Analytics plans`,
      all_stops: `Analytics all stops`,
      completed_stops: `Analytics completed stops`,
      incomplete_stops: `Analytics incomplete stops`,
      canceled_stops: `Analytics canceled stops`,
      delivery_time: `Analytics delivery time`,
      proof_of_delivery: `Analytics proof of delivery`,
      cancellation_reasons: `Analytics cancellation reasons`,
      // stops_per_zone: `Analytics stops per zone stops`,
    },
    drivers: {
      time_on_route: `Analytics time on route`,
      delivery_delays: `Analytics delivery delays`,
      route_tracking: `Analytics route tracking`,
      delivery_attempts: `Analytics delivery attempts`,
      satisfaction: `Analytics drivers satisfaction`,
    },
    customers: {
      satisfaction: `Analytics customers satisfaction`,
      sent_email: `Analytics sent emails`,
      sent_sms: `Analytics sent sms`,
    },
  },
};

class EventsPanel {
  private user: User | undefined;
  identified: any;
  initialized = false;

  get token() {
    return configuration.MIXPANEL_API_KEY as string;
  }

  init() {
    mixpanel.init(this.token, {
      loaded: () => {
        // eslint-disable-next-line
        console.info('Mixpanel is loaded');
      },
    });
    this.initialized = true;
  }

  identify(user: User) {
    this.user = user;
    try {
      if (!this.initialized) this.init();
      if (!this.identified) {
        mixpanel.people.set({
          $email: user.email,
          $name: `${user.first_name} ${user.last_name}`,
          $phone: user.phone,
          $id: user.id,
          'Organization ID': user.organization_id,
        });
        mixpanel.identify(user.id);
        this.identified = true;
      }
    } catch (err) {
      logger.error(err);
    }
  }

  validate() {
    try {
      if (!this.initialized) this.init();
      if (this.user && !sessionService.getAsUser()) {
        if (this.user) this.identify(this.user);
        return true;
      }
      return false;
    } catch (_) {
      return false;
    }
  }

  pageView(pageName: any, pageVariant?: string, extraContent = {}) {
    try {
      if (this.validate()) {
        mixpanel.register({ Platform: `web` });
        mixpanel.track(`Page View`, {
          'Page Name': pageName,
          ...(pageVariant && pageVariant !== `` ? { 'Page Variant': pageVariant } : {}),
          ...extraContent,
        });
      }
    } catch (err) {
      logger.error(err);
    }
  }

  login() {
    try {
      if (this.validate()) {
        mixpanel.track(`Login`, { 'Login Method': `E-mail` });
      }
    } catch (err) {
      logger.error(err);
    }
  }

  // This functions complements the above login function.
  completeLogin = ({ planType }: { planType?: PlanType }) => {
    try {
      mixpanel.register({ 'Organization Subscription Type': planType ?? `Free` });
      mixpanel.people.set({ 'Organization Subscription Type': planType ?? `Free` });
    } catch (err) {
      logger.error(err);
    }
  };

  logout() {
    try {
      if (this.initialized) mixpanel.reset();
      this.initialized = false;
      this.identified = false;
    } catch (err) {
      logger.error(err);
    }
  }

  geocode(address: string) {
    try {
      if (this.validate()) {
        mixpanel.register({ Platform: `web` });
        mixpanel.track(`Geocode`, {
          Address: address,
        });
      }
    } catch (err) {
      logger.error(err);
    }
  }

  geocodeAutocomplete(address: string) {
    try {
      if (this.validate()) {
        mixpanel.register({ Platform: `web` });
        mixpanel.track(`Geocode Autocomplete`, {
          Address: address,
        });
      }
    } catch (err) {
      logger.error(err);
    }
  }

  geocodeReverseGeocode(coordinates: any) {
    try {
      if (this.validate()) {
        mixpanel.register({ Platform: `web` });
        mixpanel.track(`Geocode Autocomplete`, {
          Coordinates: coordinates,
        });
      }
    } catch (err) {
      logger.error(err);
    }
  }

  registerOrganization({
    firstName,
    lastName,
    email,
    phone,
    organizationId,
    ownerId,
    createdAt,
    registerUtmInfo,
    language,
  }: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    organizationId: string;
    ownerId: string;
    createdAt?: Date;
    registerUtmInfo: Record<string, string>;
    language: Language;
  }) {
    try {
      if (!this.initialized) this.init();
      mixpanel.people.append({
        $name: `${firstName ?? ``} ${lastName ?? ``}`,
        $email: email,
        $id: ownerId,
        $phone: phone,
        'Organization ID': organizationId,
        'Registered at': createdAt,
        ...(registerUtmInfo
          ? {
              'Utm campaign': registerUtmInfo[`utm_campaign`],
              'Utm content': registerUtmInfo[`utm_content`],
              'Utm source': registerUtmInfo[`utm_source`],
              'Utm term': registerUtmInfo[`utm_term`],
              'Utm medium': registerUtmInfo[`utm_medium`],
              'Utm id': registerUtmInfo[`utm_id`],
              'Utm gclid': registerUtmInfo[`utm_gclid`],
            }
          : {}),
      });
      mixpanel.track(`Register`);

      intercom.boot();
      intercom.setUser({
        name: `${firstName} ${lastName}`,
        email,
        phone,
        language: language.value,
        ...(registerUtmInfo
          ? {
              'Utm campaign': registerUtmInfo[`utm_campaign`],
              'Utm content': registerUtmInfo[`utm_content`],
              'Utm source': registerUtmInfo[`utm_source`],
              'Utm term': registerUtmInfo[`utm_term`],
              'Utm medium': registerUtmInfo[`utm_medium`],
              'Utm id': registerUtmInfo[`utm_id`],
              'Utm gclid': registerUtmInfo[`utm_gclid`],
            }
          : {}),
      });
    } catch (err) {
      logger.error(err);
    }
  }

  onboardingPageView({
    page,
    data,
  }: {
    page:
      | `start`
      | `companyName`
      | `companyLocation`
      | `numVehicles`
      | `goal`
      | `section`
      | `constraints`
      | `vehiclesTimeWindow`
      | `vehiclesLocation`
      | `createVehicles`
      | `finish`;
    data?: any;
  }) {
    try {
      if (this.validate()) {
        mixpanel.track(`Onboarding Page View`, {
          page,
          ...(data ?? {}),
        });
      }
      intercom.track(INTERCOM_EVENTS.organization.onboarding_page_view, {
        page,
        ...(data ?? {}),
      });
    } catch (err) {
      logger.error(err);
    }
  }

  onboardingDone({
    firstName,
    lastName,
    country,
    email,
    phone,
    organizationName,
    organizationId,
    ownerId,
    createdAt,
    language,
    numOfVehicles,
    goal,
    section,
  }: {
    firstName: any;
    lastName: any;
    country: any;
    email: any;
    phone: any;
    organizationName: any;
    organizationId: any;
    ownerId: any;
    createdAt: any;
    language: any;
    numOfVehicles: any;
    goal: any;
    section: any;
  }) {
    try {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'ga_send_event_onboarding_completed' does not exist o... Remove this comment to see the full error message
      window.ga_send_event_onboarding_completed(email, phone);

      if (!this.initialized) this.init();
      mixpanel.people.append({
        $name: `${firstName ?? ``} ${lastName ?? ``}`,
        $email: email,
        $id: ownerId,
        $phone: phone,
        Country: country,
        'Organization Name': organizationName,
        'Organization ID': organizationId,
        'Registered at': createdAt,
        'Num of Vehicles': numOfVehicles,
        Goal: goal,
        Section: section,
      });
      mixpanel.track(`Onboarding done`);

      intercom.setUser({
        email,
        ...(firstName && lastName ? { name: `${firstName} ${lastName}` } : {}),
        firstname: firstName,
        lastname: lastName,
        company: organizationName,
        phone,
        language,
        numOfVehicles,
        goal,
        section,
      });
    } catch (err) {
      logger.error(err);
    }
  }

  planCreated() {
    try {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'ga_send_event_plan_created' does not exist o... Remove this comment to see the full error message
      window.ga_send_event_plan_created(this.user.email, this.user.phone);

      if (this.validate()) mixpanel.track(`Plan Created`);
    } catch (err) {
      console.error(err);
    }
  }

  planUpdated(status: any) {
    try {
      if (this.validate()) mixpanel.track(`Plan Updated`, { Status: status });
    } catch (err) {
      logger.error(err);
    }
  }

  planDispatch() {
    try {
      if (this.validate()) mixpanel.track(`Plan Dispatching`);
      intercom.track(INTERCOM_EVENTS.plan.dispatched);
    } catch (err) {
      logger.error(err);
    }
  }

  planDispatchAction(
    action:
      | `dispatch_copy_link`
      | `dispatch_open_link`
      | `dispatch_send_whatsapp`
      | `dispatch_send_email`
      | `dispatch_send_email_to_all`
  ) {
    try {
      if (this.validate()) mixpanel.track(`Plan Dispatching Action`, { action });
      intercom.track(INTERCOM_EVENTS.plan[action]);
    } catch (err) {
      logger.error(err);
    }
  }

  planMenu(view: string) {
    try {
      if (this.validate()) {
        const View = view && view.length > 1 && view.charAt(0).toUpperCase() + view.slice(1);
        if (view) mixpanel.track(`Plan Menu`, { View });
      }
    } catch (err) {
      logger.error(err);
    }
  }

  stopActionsMenu(action: string) {
    try {
      if (this.validate()) {
        mixpanel.track(`Stop Menu Actions`, { action });
      }
    } catch (err) {
      logger.error(err);
    }
  }

  geoFenceActionsMenu(action: string) {
    try {
      if (this.validate()) {
        mixpanel.track(`GeoFence Menu Actions`, { action });
      }
    } catch (err) {
      logger.error(err);
    }
  }

  driverActionsMenu(action: string) {
    try {
      if (this.validate()) {
        mixpanel.track(`Driver Menu Actions`, { action });
      }
    } catch (err) {
      logger.error(err);
    }
  }

  supervisorActionsMenu(action: string) {
    try {
      if (this.validate()) {
        mixpanel.track(`Supervisor Menu Actions`, { action });
      }
    } catch (err) {
      logger.error(err);
    }
  }

  planOptimized({
    numServices,
    numRoutes,
    distance,
    duration,
  }: {
    numServices?: number;
    numRoutes?: number;
    distance?: number;
    duration?: number;
  }) {
    try {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'ga_send_event_plan_optimized' does not exist o... Remove this comment to see the full error message
      window.ga_send_event_plan_optimized(this.user.email, this.user.phone);

      intercom.track(INTERCOM_EVENTS.plan.optimized);
      if (this.validate()) {
        mixpanel.track(`Plan Optimized`, {
          ...(numServices ? { '# of Services': numServices } : {}),
          ...(numRoutes ? { '# of Rutas': numRoutes } : {}),
          ...(distance ? { Distance: distance } : {}),
          ...(duration ? { 'Duration (min)': duration / 60 } : {}),
        });
      }
    } catch (err) {
      logger.error(err);
    }
  }

  routeOptimized(numServices: any, distance: any, duration: any) {
    try {
      intercom.track(INTERCOM_EVENTS.plan.route.optimized);
      if (this.validate()) {
        mixpanel.track(`Route Optimized`, {
          '# of Services': numServices,
          Distance: distance,
          'Duration (min)': duration / 60,
        });
      }
    } catch (err) {
      logger.error(err);
    }
  }

  createDrivers(via: any) {
    try {
      if (this.validate()) mixpanel.track(`Create Drivers`, { Via: via });
    } catch (err) {
      logger.error(err);
    }
  }

  createStops(via: any) {
    try {
      if (this.validate()) mixpanel.track(`Create Stops`, { Via: via });
    } catch (err) {
      logger.error(err);
    }
  }

  addClient(via: any) {
    try {
      intercom.track(INTERCOM_EVENTS.assets.clients.created);
      if (this.validate()) mixpanel.track(`Add Client`, { Via: via });
    } catch (err) {
      logger.error(err);
    }
  }

  addVehicle(via: any) {
    try {
      intercom.track(INTERCOM_EVENTS.assets.vehicles.created);
      if (this.validate()) mixpanel.track(`Add Vehicle`, { Via: via });
    } catch (err) {
      logger.error(err);
    }
  }

  routesExceeded(inUse: number, total: number) {
    try {
      intercom.track(INTERCOM_EVENTS.project.routes_exceeded, { inUse, total });
      if (this.validate()) mixpanel.track(`Routes Exceeded`, { inUse, total });
    } catch (err) {
      logger.error(err);
    }
  }

  changeSubscriptionPlan(oldPlan: any, newPlan: any, oldVehicles: any, newVehicles: any, remainingDays: any) {
    try {
      if (this.validate()) {
        mixpanel.register({
          'Organization Subscription Type': newPlan,
          'Number of Vehicles': newVehicles,
        });

        mixpanel.people.set({
          'Organization Subscription Type': newPlan,
          'Days Until Trial Ends': remainingDays,
        });

        let change = null;
        if (oldPlan !== newPlan) {
          change =
            (oldPlan === PLAN_TYPE.MONKEY && (newPlan === PLAN_TYPE.GORILLA || newPlan === PLAN_TYPE.KONG)) ||
            (oldPlan === PLAN_TYPE.GORILLA && newPlan === PLAN_TYPE.KONG)
              ? `Upgrade`
              : `Downgrade`;
        } else if (oldVehicles !== newVehicles) {
          change = newVehicles > oldVehicles ? `Upgrade` : `Downgrade`;
        }

        mixpanel.track(`Change Subscription`, {
          'Old Subscription': oldPlan,
          'New Subscription': newPlan,
          'Old Number of Vehicles': oldVehicles,
          'New Number of Vehicles': newVehicles,
          ...(change !== null ? { 'Subscription Change': change } : {}),
        });
      }
    } catch (err) {
      logger.error(err);
    }
  }

  cancelSubscriptionPlan({
    mainReason,
    otherReason,
    otherSolutionChosen,
    opinion,
    planType,
  }: {
    mainReason: string;
    otherReason?: string;
    otherSolutionChosen?: string;
    opinion?: string;
    planType: PlanType;
  }) {
    try {
      if (this.validate()) {
        mixpanel.track(`Cancel Subscription`, {
          'Cancelation Method': `Manual`,
          'Cancelation Reason': mainReason,
          ...(otherReason ? { 'Other Reason': otherReason } : {}),
          ...(otherSolutionChosen ? { 'Other solution chosen': otherSolutionChosen } : {}),
          ...(opinion ? { Opinion: opinion } : {}),
          'Canceled Subscription Type': planType,
        });
        mixpanel.people.set({
          'Organization Subscription Type': planType,
          'Cancelation Reason': mainReason,
          ...(otherReason ? { 'Other Reason': otherReason } : {}),
          ...(otherSolutionChosen ? { 'Other solution chosen': otherSolutionChosen } : {}),
          ...(opinion ? { Opinion: opinion } : {}),
        });
      }

      intercom.track(INTERCOM_EVENTS.organization.organization_cancel_subscription, {
        'Cancelation Method': `Manual`,
        'Cancelation Reason': mainReason,
        ...(otherReason ? { 'Other Reason': otherReason } : {}),
        ...(otherSolutionChosen ? { 'Other solution chosen': otherSolutionChosen } : {}),
        ...(opinion ? { Opinion: opinion } : {}),
        'Canceled Subscription Type': planType,
      });
      intercom.setUser({
        'Organization Subscription Type': planType,
        'Cancelation Reason': mainReason,
        ...(otherReason ? { 'Other Reason': otherReason } : {}),
        ...(otherSolutionChosen ? { 'Other solution chosen': otherSolutionChosen } : {}),
        ...(opinion ? { Opinion: opinion } : {}),
      });
    } catch (err) {
      logger.error(err);
    }
  }

  plansSearch() {
    try {
      if (this.validate()) mixpanel.track(`Plans Search`);
      intercom.track(INTERCOM_EVENTS.plans.search);
    } catch (err) {
      logger.error(err);
    }
  }

  createProjectGeoFence(geoFence: GeoFenceDTO) {
    try {
      if (this.validate()) {
        mixpanel.track(`New project geo fence`, {
          'Project ID': geoFence.project_id,
          'GeoFence ID': geoFence.id,
          'GeoFence label': geoFence.label,
          'GeoFence color': geoFence.color,
          'GeoFence enabled': geoFence.enabled,
          'GeoFence coordinates': geoFence.coordinates,
        });
      }
    } catch (err) {
      logger.error(err);
    }
  }

  createStopsCount(length: number) {
    try {
      intercom.track(INTERCOM_EVENTS.plan.service.created, { number: length });
      if (this.validate()) mixpanel.track(`Create Stops Count`, { number: length });
    } catch (err) {
      logger.error(err);
    }
  }

  createStopTask(task: Task) {
    try {
      if (this.validate()) {
        mixpanel.track(`New stop task`, {
          'Stop ID': task.stop_id,
          'Task ID': task.id,
          'Task status': task.status,
          'Task label': task.label,
          'Task barcode': task.barcode,
          'Task comments': task.comments,
          'Task custom_fields': task.custom_fields,
        });
      }
    } catch (err) {
      logger.error(err);
    }
  }

  projectCommunication({
    action,
    data = {},
  }: {
    action: `edit_sms` | `edit_email` | `enable_sms_request`;
    data?: any;
  }) {
    try {
      if (this.validate()) mixpanel.track(`Project Communication`, { action, data });
      intercom.track(INTERCOM_EVENTS.project.communication[action], data);
    } catch (err) {
      logger.error(err);
    }
  }
}

const eventsPanel = new EventsPanel();

export default eventsPanel;
